// packages/console/src/hooks/useLiveStream.ts
//
// React binding over the single live-session SSE stream (api/live streamLiveSession).
// It owns one connection for the component tree's lifetime and exposes the
// latest typed state per event kind, keyed so a re-emitted link/rung/dest
// REPLACES its prior sample rather than accumulating duplicates:
//   stream     -> the session-wide sample (latest wins)
//   links      -> Map<linkId, LiveLink>, insertion-ordered
//   rungs      -> Map<rungName, LiveRung>, insertion-ordered
//   dests      -> Map<destId, LiveDest>, insertion-ordered
//   hw         -> the latest hardware sample
//   aggregate  -> the sticky fleet roll-up
//   status     -> the sticky connection status
//   logs       -> a bounded tail of pipeline log lines
//   conn       -> the SSE transport state, for honest loading/empty/error
//
// The maps are replaced with a fresh identity on every update (never mutated in
// place) so a memoized consumer keyed on the map sees the change under React's
// referential-equality check. With no session the stream yields nothing and
// every field stays at its empty default: the panels then render the design's
// stopped / No-program / em-dash state, not fabricated numbers (R10).

import { useEffect, useState } from "react";
import {
  streamLiveSession,
  type LiveAggregate,
  type LiveDest,
  type LiveHw,
  type LiveLink,
  type LiveLog,
  type LiveRung,
  type LiveStatus,
  type LiveStream,
} from "../api/live";
import type { SseStatus } from "../api/sse";

export interface LiveStreamState {
  stream: LiveStream | null;
  links: Map<string, LiveLink>;
  rungs: Map<string, LiveRung>;
  dests: Map<string, LiveDest>;
  hw: LiveHw | null;
  aggregate: LiveAggregate | null;
  status: LiveStatus | null;
  logs: LiveLog[];
  // dropped is the running count of samples the server shed under backpressure
  // ("dropped" event). A non-zero value marks the feed as lossy.
  dropped: number;
  conn: SseStatus;
}

// sessionState resolves the authoritative live session state: the latest full
// "stream" sample wins, and the sticky "status" event (replayed on every
// reconnect) is the fallback before the first stream sample lands. Absent both
// there is no session, so it reads "stopped" (the honest empty state).
export function sessionState(state: LiveStreamState): LiveStream["state"] {
  if (state.stream) return state.stream.state;
  if (state.status) return state.status.state;
  return "stopped";
}

// upsert returns a fresh Map with key set to value, preserving insertion order
// (a re-emit of an existing key updates in place without reordering the row).
function upsert<V>(prev: Map<string, V>, key: string, value: V): Map<string, V> {
  const next = new Map(prev);
  next.set(key, value);
  return next;
}

export function useLiveStream(logCap = 200): LiveStreamState {
  const [stream, setStream] = useState<LiveStream | null>(null);
  const [links, setLinks] = useState<Map<string, LiveLink>>(new Map());
  const [rungs, setRungs] = useState<Map<string, LiveRung>>(new Map());
  const [dests, setDests] = useState<Map<string, LiveDest>>(new Map());
  const [hw, setHw] = useState<LiveHw | null>(null);
  const [aggregate, setAggregate] = useState<LiveAggregate | null>(null);
  const [status, setStatus] = useState<LiveStatus | null>(null);
  const [logs, setLogs] = useState<LiveLog[]>([]);
  const [dropped, setDropped] = useState(0);
  const [conn, setConn] = useState<SseStatus>("connecting");

  useEffect(() => {
    const c = streamLiveSession({
      onStream: setStream,
      onLink: (l) => setLinks((prev) => upsert(prev, l.linkId, l)),
      onRung: (r) => setRungs((prev) => upsert(prev, r.rungName, r)),
      onDest: (d) => setDests((prev) => upsert(prev, d.destId, d)),
      onHw: setHw,
      onAggregate: setAggregate,
      onStatus: setStatus,
      onLog: (l) => setLogs((prev) => (prev.length >= logCap ? [...prev.slice(1), l] : [...prev, l])),
      // The server sends a monotonically growing total, so take the latest value
      // rather than summing deltas.
      onDropped: (n) => setDropped(n),
      onConnState: setConn,
    });
    return () => c.close();
  }, [logCap]);

  return { stream, links, rungs, dests, hw, aggregate, status, logs, dropped, conn };
}
